import type { EventType } from '@sdk/schedule/types/EventType';

import type {
  EventsByDate,
  TotalsPerDay,
  SpecialGroupType,
  AvailableGroup,
  UnassignedGroup,
} from '../../types/GroupsArrayType';
import type { RowType, MinutesSummaryByDay } from '../../types/RowType';
import { UNASSIGNED_SUMMARY_HEIGHT, UNASSIGNED } from '../constants';
import generateRowId from '../helpers/generateRowId';

import getRowEvents from './getRowEvents';
import { UnassignedHeader, AvailableHeader } from './Headers';
import { markLastRow } from './markLastRow';
import getRowHeight from './getRowHeight';

type SingleEventByDate = {
  [key in string]: EventType;
};

export default function makeTopRows(
  type: SpecialGroupType,
  events: EventsByDate,
  group: UnassignedGroup | AvailableGroup,
  eventHeight: number
): Array<RowType> {
  const isUnassigned = type === UNASSIGNED;
  const header = isUnassigned ? UnassignedHeader : AvailableHeader;

  const firstRowEvents = getRowEvents(events);
  const firstRowId = `${type}FirstRow`;

  const rows: Array<RowType> = [
    header,
    {
      id: generateRowId(firstRowId, firstRowEvents),
      rowType: 'SpecialGroupFirstRow',
      rowData: {
        events: firstRowEvents,
        size: getRowHeight(eventHeight, { isFirst: true }),
        eventHeight,
        type,
        group,
      },
    },
  ];

  let rowEvents: SingleEventByDate;
  do {
    rowEvents = getRowEvents(events);
    if (rowEvents && Object.keys(rowEvents).length) {
      rows.push({
        id: generateRowId(`${type}RegularRow`, rowEvents),
        rowType: 'SpecialGroupRegularRow',
        rowData: {
          events: rowEvents,
          size: getRowHeight(eventHeight),
          eventHeight,
          type,
          group,
        },
      });
    }
  } while (Object.keys(rowEvents).length);

  markLastRow(rows);

  if (isUnassigned) {
    rows.push({
      id: 'UnassignedSummaryRow', // @TODO: check if updates on summary repaints this row
      rowType: 'UnassignedSummaryRow',
      rowData: {
        summary: getUnassignedSummary(group.totalsPerDay),
        size: UNASSIGNED_SUMMARY_HEIGHT,
      },
    });
  }

  return rows;
}

function getUnassignedSummary(totalsPerDay: TotalsPerDay): MinutesSummaryByDay {
  return Object.keys(totalsPerDay).reduce(
    (result, date) => ({ ...result, [date]: totalsPerDay[date].paidMinutes }),
    {}
  );
}
